<template>
  <div class="variant-manager">
    <div v-if="!hasVariants">
      <p class="text-muted small">
        This item is sold as a single product. Enable variations to sell it in different
        options (e.g. Color, Size) each with their own stock, price and SKU.
      </p>
      <button type="button" class="btn btn-outline-primary btn-sm" :disabled="enabling" @click="enableVariants">
        <i class="fas fa-spinner fa-spin" v-if="enabling"></i>
        Enable Variations for "{{ product.product }}"
      </button>
    </div>

    <div v-else>
      <div v-if="loading" class="text-center py-3">
        <i class="fas fa-spinner fa-spin"></i> Loading variants...
      </div>

      <div v-else>
        <table class="table table-sm" v-if="variants.length">
          <thead>
            <tr>
              <th>Variant</th>
              <th>SKU</th>
              <th>Selling Price</th>
              <th>Buying Price</th>
              <th>Stock</th>
              <th>Active</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <template v-for="variant in variants">
              <tr :key="variant.id">
                <td><input class="form-control form-control-sm" v-model="variant.name" /></td>
                <td><input class="form-control form-control-sm" v-model="variant.sku" /></td>
                <td><input type="number" step="any" min="0" class="form-control form-control-sm" v-model="variant.ppu" /></td>
                <td><input type="number" step="any" min="0" class="form-control form-control-sm" v-model="variant.wholesale_ppu" /></td>
                <td class="text-nowrap">{{ variant.remaining_units }}</td>
                <td class="text-center"><b-form-checkbox v-model="variant.is_active" switch /></td>
                <td class="text-nowrap">
                  <button type="button" class="btn btn-outline-primary btn-sm mr-1" :disabled="saving" @click="saveVariant(variant)">Save</button>
                  <button type="button" class="btn btn-outline-secondary btn-sm mr-1" @click="toggleUnits(variant)">
                    Units {{ expandedUnits === variant.id ? '▲' : '▼' }}
                  </button>
                  <button type="button" class="btn btn-outline-danger btn-sm" :disabled="saving" @click="removeVariant(variant)">Delete</button>
                </td>
              </tr>
              <tr :key="variant.id + '-units'" v-if="expandedUnits === variant.id">
                <td colspan="7" class="bg-light">
                  <unit-manager owner-type="variant" :owner-id="variant.id" />
                </td>
              </tr>
            </template>
          </tbody>
        </table>
        <p v-else class="text-muted small">No variants yet — build them below.</p>

        <div class="border rounded p-3 mt-3">
          <p class="mb-2"><strong>Build variants from attributes</strong></p>
          <div v-for="(attr, index) in attributeRows" :key="index" class="form-row align-items-end mb-2">
            <div class="form-group col-md-4 mb-0">
              <label class="small mb-1">Attribute (e.g. Color)</label>
              <input class="form-control form-control-sm" v-model="attr.name" />
            </div>
            <div class="form-group col-md-6 mb-0">
              <label class="small mb-1">Values, comma separated (e.g. Black, White, Red)</label>
              <input class="form-control form-control-sm" v-model="attr.values" />
            </div>
            <div class="form-group col-md-2 mb-0">
              <button type="button" class="btn btn-outline-danger btn-sm" @click="attributeRows.splice(index, 1)">Remove</button>
            </div>
          </div>
          <button type="button" class="btn btn-outline-secondary btn-sm" @click="attributeRows.push({ name: '', values: '' })">
            + Add Attribute
          </button>
          <button type="button" class="btn btn-primary btn-sm ml-2" :disabled="generating" @click="generateCombinations">
            Generate Combinations
          </button>
        </div>

        <div v-if="combinations.length" class="border rounded p-3 mt-3">
          <p class="mb-2"><strong>Review &amp; create {{ combinations.length }} variant(s)</strong></p>
          <table class="table table-sm">
            <thead>
              <tr>
                <th>Name</th>
                <th>SKU</th>
                <th>Selling Price</th>
                <th>Buying Price</th>
                <th>Starting Stock</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(combo, index) in combinations" :key="index">
                <td><input class="form-control form-control-sm" v-model="combo.name" /></td>
                <td><input class="form-control form-control-sm" v-model="combo.sku" placeholder="auto-generated" /></td>
                <td><input type="number" step="any" min="0" class="form-control form-control-sm" v-model="combo.ppu" /></td>
                <td><input type="number" step="any" min="0" class="form-control form-control-sm" v-model="combo.wholesale_ppu" /></td>
                <td><input type="number" step="any" min="0" class="form-control form-control-sm" v-model="combo.remaining_units" /></td>
              </tr>
            </tbody>
          </table>
          <button type="button" class="btn btn-primary btn-sm" :disabled="creating" @click="createAllVariants">
            <i class="fas fa-spinner fa-spin" v-if="creating"></i>
            Create {{ combinations.length }} Variant(s)
          </button>
          <button type="button" class="btn btn-link btn-sm text-muted" @click="combinations = []">Cancel</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import UnitManager from "./UnitManager.vue";

/**
 * Variant enable/build/CRUD UI. Self-contained — reads and writes only via
 * ProductVariantController's routes. See
 * docs/product-variations-units-implementation-plan.md Part 16.
 */
export default {
  name: "VariantManager",
  components: { UnitManager },
  props: {
    product: { type: Object, required: true },
  },
  data() {
    return {
      hasVariants: !!this.product.has_variants,
      variants: [],
      loading: false,
      saving: false,
      enabling: false,
      generating: false,
      creating: false,
      expandedUnits: null,
      attributeRows: [{ name: "", values: "" }],
      combinations: [],
    };
  },
  watch: {
    "product.id": {
      immediate: true,
      handler() {
        this.hasVariants = !!this.product.has_variants;
        if (this.hasVariants) this.fetchVariants();
      },
    },
  },
  methods: {
    async fetchVariants() {
      this.loading = true;
      try {
        const result = await axios.get(`/api/products/${this.product.id}/variants`);
        this.hasVariants = result.data.has_variants;
        this.variants = result.data.variants;
      } catch (e) {
        this.$toasts && this.$toasts.push({ type: "error", message: "Failed to load variants" });
      } finally {
        this.loading = false;
      }
    },
    async enableVariants() {
      this.enabling = true;
      try {
        const result = await axios.post(`/api/products/${this.product.id}/variants/enable`, {
          default_variant_name: "Default",
        });
        this.hasVariants = true;
        this.$toasts && this.$toasts.push({ type: "success", message: result.data.message });
        this.$emit("enabled");
        await this.fetchVariants();
      } catch (e) {
        const message = e.response && e.response.data ? e.response.data.message : "Failed to enable variations";
        this.$toasts && this.$toasts.push({ type: "error", message });
      } finally {
        this.enabling = false;
      }
    },
    toggleUnits(variant) {
      this.expandedUnits = this.expandedUnits === variant.id ? null : variant.id;
    },
    async saveVariant(variant) {
      this.saving = true;
      try {
        const payload = {
          name: variant.name,
          sku: variant.sku,
          ppu: variant.ppu,
          wholesale_ppu: variant.wholesale_ppu,
          is_active: variant.is_active,
        };
        await axios.put(`/api/product-variants/${variant.id}`, payload);
        this.$toasts && this.$toasts.push({ type: "success", message: "Variant updated" });
        this.$emit("changed");
      } catch (e) {
        const message = e.response && e.response.data ? e.response.data.message : "Failed to update variant";
        this.$toasts && this.$toasts.push({ type: "error", message });
      } finally {
        this.saving = false;
      }
    },
    async removeVariant(variant) {
      if (!confirm(`Delete variant "${variant.name}"? This cannot be undone.`)) return;
      this.saving = true;
      try {
        await axios.delete(`/api/product-variants/${variant.id}`);
        this.variants = this.variants.filter(v => v.id !== variant.id);
        this.$toasts && this.$toasts.push({ type: "success", message: "Variant deleted" });
        this.$emit("changed");
      } catch (e) {
        const message = e.response && e.response.data ? e.response.data.message : "Failed to delete variant";
        this.$toasts && this.$toasts.push({ type: "error", message });
      } finally {
        this.saving = false;
      }
    },
    async generateCombinations() {
      const attributes = this.attributeRows
        .filter(a => a.name && a.values)
        .map(a => ({ name: a.name.trim(), values: a.values.split(",").map(v => v.trim()).filter(Boolean) }));

      if (!attributes.length) {
        this.$toasts && this.$toasts.push({ type: "error", message: "Add at least one attribute with values" });
        return;
      }

      this.generating = true;
      try {
        const result = await axios.post(`/api/products/${this.product.id}/variants/generate-combinations`, { attributes });
        this.combinations = result.data.combinations.map(combo => ({
          attributes: combo,
          name: Object.values(combo).join(" / "),
          sku: "",
          ppu: this.product.ppu || null,
          wholesale_ppu: this.product.wholesale_ppu || null,
          remaining_units: 0,
        }));
      } catch (e) {
        const message = e.response && e.response.data ? e.response.data.message : "Failed to generate combinations";
        this.$toasts && this.$toasts.push({ type: "error", message });
      } finally {
        this.generating = false;
      }
    },
    async createAllVariants() {
      this.creating = true;
      try {
        for (const combo of this.combinations) {
          await axios.post(`/api/products/${this.product.id}/variants`, {
            name: combo.name,
            attributes: combo.attributes,
            sku: combo.sku || undefined,
            ppu: combo.ppu,
            wholesale_ppu: combo.wholesale_ppu,
            remaining_units: combo.remaining_units,
          });
        }
        this.$toasts && this.$toasts.push({ type: "success", message: `${this.combinations.length} variant(s) created` });
        this.combinations = [];
        this.attributeRows = [{ name: "", values: "" }];
        this.$emit("changed");
        await this.fetchVariants();
      } catch (e) {
        const message = e.response && e.response.data ? e.response.data.message : "Failed to create variants";
        this.$toasts && this.$toasts.push({ type: "error", message });
      } finally {
        this.creating = false;
      }
    },
  },
};
</script>
